import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import { registerSWUpdate } from "./sw-update";
// Boot marker for the index.html watchdog (poisoned-SW self-heal): proves
// the main module actually executed.
(window as any).__APP_BOOTED = true; // build-salt 20260710a — forces new chunk hash past a poisoned edge URL (FRA colo cached HTML as ahp-main js+css during deploy propagation)

import { isNativeApp } from "./lib/platform";

// ── Sentry — production error monitoring ─────────────────────────────────────
// Init runs once at app boot. DSN is a public client-side key (intentional).
// Release tag flows from CF_PAGES_COMMIT_SHA via vite.config.ts → import.meta.env.VITE_RELEASE
// so client events tag the same release as the uploaded source maps. Without
// that match, Sentry can't symbolicate stack traces.
// Replay is opt-out — text masked, media blocked — AHP is anonymous-by-design.
Sentry.init({
  dsn: "https://a721157bd669ed673b454d8c267ba117@o56206.ingest.us.sentry.io/4511382454796288",
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_RELEASE,
  sendDefaultPii: false,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
  ],
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event) {
    // Local kill-switch — visitors can opt out by setting sentry_off=1 in localStorage.
    if (typeof window !== "undefined" && window.localStorage?.getItem("sentry_off") === "1") {
      return null;
    }
    return event;
  },
});

// Expose for console-eval verification (`Sentry.captureException(new Error("test"))`).
if (typeof window !== "undefined") {
  (window as unknown as { Sentry?: typeof Sentry }).Sentry = Sentry;
}

// ── Minimal Crashed fallback for the top-level ErrorBoundary ─────────────────
function Crashed() {
  return (
    <div style={{
      minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center",
      padding: 32, fontFamily: '"Instrument Sans", system-ui, -apple-system, sans-serif',
      color: "#1B1B1B", background: "#FAF9F6", textAlign: "center",
    }}>
      <div style={{ maxWidth: 480 }}>
        <h1 style={{
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontSize: 48, fontWeight: 400, lineHeight: 1.1, marginBottom: 16, color: "#002868",
        }}>
          Something went wrong.
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.5, color: "rgba(27,27,27,0.65)", marginBottom: 24 }}>
          The error has been reported. Reload to get back in.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: "12px 24px", borderRadius: 10, border: "none", cursor: "pointer", background: "#B22234", color: "#fff", fontSize: 16, fontWeight: 600, fontFamily: '"Instrument Sans", system-ui, sans-serif' }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

// ── Console banner for curious developers ──
// Inspired by classic dev-console Easter eggs from GitHub, Airbnb, etc.
(() => {
  const big = 'font-family:"Instrument Serif",Georgia,serif;font-size:48px;font-weight:400;color:#B22234;line-height:1;letter-spacing:-1px;';
  const mid = 'font-family:"Instrument Sans",-apple-system,sans-serif;font-size:14px;color:#002868;font-weight:600;letter-spacing:0.5px;';
  const small = 'font-family:"Instrument Sans",-apple-system,sans-serif;font-size:12px;color:#1B1B1B;';
  const muted = 'font-family:"Instrument Sans",-apple-system,sans-serif;font-size:11px;color:rgba(27,27,27,0.62);font-style:italic;';
  const gold = 'font-family:"Instrument Sans",-apple-system,sans-serif;font-size:13px;color:#C5A55A;font-weight:600;';

  console.log('%cSORRY, NOT HIRING.', big);
  console.log('%cBUT WE APPRECIATE YOU LOOKING.', mid);
  console.log('%c ', small);
  console.log(
    '%cWe\'re an independent, consumer-first project fighting hospital price opacity.\n' +
    'No VC. No investors. No job openings — for now.\n\n' +
    'If you want to help the cause:\n' +
    '  • Share a hospital bill horror story → americanhospitalprices.com/stories\n' +
    '  • Use the data, file FOIA requests, talk to your state AG\n' +
    '  • Support the project → americanhospitalprices.com/donate',
    small
  );
  console.log('%c ', small);
  console.log('%c★ Hospitals are legally required to publish their prices. We hold them accountable. ★', gold);
  console.log('%c ', small);
  console.log('%c— Built for the American people.', muted);
  console.log('%c ', small);
})();

// Tag the document for native-only CSS (16px inputs to stop iOS focus auto-zoom,
// which is what visually shoves a position:fixed header when the keyboard opens).
if (isNativeApp() && typeof document !== "undefined") {
  document.documentElement.classList.add("native-app");
}

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={<Crashed />} showDialog={false}>
    <App />
  </Sentry.ErrorBoundary>
);

// ─── Boot splash lifecycle (web + native) ───────────────────────────────────
// The splash HOLDS until the app is genuinely interactive — flag video
// decodable + main thread drained — because dismissing at first paint left a
// 2–3s window where the page was visible but touch-dead (bundle hydration +
// offline-data decode still hogging the main thread). Native gets a longer
// cap (local assets, bigger decode). The inline 12s fallback in index.html
// still removes the splash no matter what.
//
// Mascot: the inline SVG paints at frame zero (nothing else can — the JS
// hasn't parsed yet), then the real Dignity GLB fades in over it once
// model-viewer has it. dignity_bill_lite.glb is the splash asset everywhere:
// 1.6MB (quantized + simplified from the 24MB master, visually identical at
// splash size) — local disk on native, one CDN fetch then browser-cached on
// web. In-place stacking + loading="eager" matter: model-viewer's default
// lazy loading never fires for offscreen containers. WebGL/load failure ⇒
// the SVG simply stays.
const killSplash = () => (window as unknown as { __bootSplashKill?: () => void }).__bootSplashKill?.();

/** Set when the 3D mascot becomes visible — the dismissal gate guarantees it
 *  a minimum on-screen dwell so it never flashes in right as the splash exits. */
let splashModelShownAt = 0;

// The mascot <model-viewer> lives directly in index.html and is driven by the
// standalone /model-viewer.min.js script — it renders in parallel with (not
// after) this bundle. Here we only OBSERVE it for the dwell gate below.
const splashModelReady: Promise<void> = new Promise<void>((resolve) => {
  const mv = document.querySelector('#boot-splash model-viewer') as (HTMLElement & { loaded?: boolean }) | null;
  if (!mv) { resolve(); return; }
  if (mv.loaded) { splashModelShownAt = Date.now(); resolve(); return; }
  mv.addEventListener('load', () => { splashModelShownAt = Date.now(); resolve(); }, { once: true });
  mv.addEventListener('error', () => resolve(), { once: true });
});

(async () => {
  const native = isNativeApp();
  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  const deadline = Date.now() + (native ? 6500 : 4500); // hard cap — never trap the user
  // Fonts (bundled locally on native — near-instant).
  try { await Promise.race([document.fonts?.ready, sleep(1200)]); } catch { /* ok */ }
  // Flag video: wait briefly for the element to mount (deep links to pages
  // without it skip ahead), then until it's decodable.
  let video: HTMLVideoElement | null = null;
  const findUntil = Date.now() + (native ? 1800 : 1200);
  while (Date.now() < findUntil && !(video = document.querySelector('video[data-boot-critical]'))) {
    await sleep(120);
  }
  while (video && Date.now() < deadline && video.readyState < 3) {
    await sleep(120);
  }
  // Give the 3D mascot its moment (sub-capped — never blocks past this).
  await Promise.race([splashModelReady, sleep(Math.max(0, Math.min(deadline - Date.now(), 2500)))]);
  // Let hydration / offline-data decode drain so the first touch isn't eaten.
  await new Promise<void>((r) => {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
    if (ric) ric(() => r(), { timeout: 1500 }); else setTimeout(r, 400);
  });
  // Minimum dwell: if the 3D Dignity just appeared, keep the splash up long
  // enough to actually see it (never flash-in-flash-out), capped by deadline.
  if (splashModelShownAt) {
    const dwellLeft = 1400 - (Date.now() - splashModelShownAt);
    if (dwellLeft > 0) await sleep(Math.min(dwellLeft, Math.max(0, deadline - Date.now())));
  }
  await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
  killSplash();
})();

// Service worker: web only. In the native shell the bundled .dat data handles
// offline, and a SW would serve a stale JS bundle across app builds (the app
// would keep running old code). Tear down any SW + caches left over from a
// prior build so the freshly-installed bundle always runs.
if (isNativeApp()) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
  }
  if ('caches' in window) {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
  }
} else {
  // Register service worker update handler (also enables push notifications)
  registerSWUpdate();
}
